// FaShip 已随「散货板块」下线一并移出（见下方下线说明）；恢复时需重新加回本行。
import {
  FaHome, FaCubes, FaBell, FaInfoCircle,
  FaTachometerAlt, FaUserCircle, FaPlusCircle, FaBox, FaWallet, FaHeadset,
} from 'react-icons/fa'

/**
 * 站点结构的唯一来源。顶部导航、账号菜单、页脚、面包屑全部从这里派生，
 * 改一处即三处同步。
 *
 * 【为什么要有这个文件】
 * 在此之前站点结构有三份互相独立的拷贝：Navbar 的两个数组、Footer 的链接清单、
 * 以及 20 多个页面各自写死的 breadcrumb={[...]}。2026-08-01 把「我的」并入账号
 * 菜单时，改了 MyOrdersPage / OrderCreatePage 的面包屑，却漏了 FinancePage 与
 * IssuesPage —— 它们仍写着「客户中心 / …」，与导航对不上。这类漏改是结构性的，
 * 只要还有第二份拷贝就会反复发生，所以收敛成一份。
 *
 * 【节点字段】
 *   path            路由地址。分组节点（集运板块、我的）没有 path，
 *                   在面包屑里渲染成纯文本、不可点击
 *   label           导航与页脚里显示的文案
 *   icon            仅导航用
 *   children        下级节点
 *   aliases         同一个页面的其它路由。App.jsx 里 /cargo 与 /my-consolidation、
 *                   /finance 与 /portal/finance 各是同一组件的两个路由，
 *                   走别名进来也要能解析出面包屑
 *   breadcrumbLabel 面包屑文案与导航文案不同时才写（见 /notifications）
 *
 * 【新增页面时】
 * 在这里加一个节点即可，页面里调 useBreadcrumb() 就有面包屑，不要再往页面里
 * 写死数组。不进导航但需要面包屑的页面放 extraNav。
 */

export const mainNav = [
  { path: '/', label: '首页', icon: FaHome },
  {
    label: '集运板块',
    icon: FaCubes,
    children: [
      { path: '/consolidation', label: '集运报名' },
      // /cargo 是 App.jsx 里指向同一个 CargoPage 的另一条路由
      { path: '/my-consolidation', label: '我的报名', aliases: ['/cargo'] },
      // 运费估算紧跟「我的报名」：它是四个页面里唯一端到端跑通的（后台「配置管理」
      // 维护价格与国家，前端实时算），使用频次最高，所以排在只有壳的「集运订单」前面。
      { path: '/freight-calculator', label: '运费估算' },
      { path: '/consolidation-order', label: '集运订单' },
      { path: '/inspection', label: '验货查询' },
      { path: '/tracking', label: '轨迹查询' },
    ],
  },
  /*
   * 【散货板块已下线 · 2026-07-30】
   *
   * 产品决定不再对外提供散货拼柜业务，但代码一行未删，以备后续恢复 ——
   * 所以这里是注释而不是删除。恢复时把本段取消注释即可。
   *
   * 「签收确认」(/cargo-sign) 属于本板块，一并下线（页面已不需要）。
   *
   * 同一批下线的入口（改动方式相同，都是注释保留）：
   *   - App.jsx                 6 条散货路由及对应的 import（含 /cargo-sign）
   *   - Footer.jsx              「散货拼柜」栏目（见本文件 footerSections 里的注释块）
   *   - HomePage.jsx            核心服务里的「散货拼柜」卡片
   *   - DashboardPage.jsx       quickLinks 里的「散货拼柜」
   * 后台管理端同步下线：blade_menu 的 portal_lcl 置 is_deleted=1
   * （见 admin-backend/doc/sql/7.30-offline-lcl-menu.sql），代码同样保留。
   *
   * 页面组件仍在 src/pages/ 下：FreightEvaluationPage、CargoSearchPage、
   * CargoSignPage、CargoClaimPage、LCLPage —— 各自文件头部也有下线说明。
   * 这五个页面的面包屑**仍是写死的**：路由已注释、页面不可达，改接 useBreadcrumb
   * 也解析不出东西。恢复本板块时，把下面这段取消注释，再把那五个页面一并改接。
   *
   * 注意：散货的「轨迹查询」(/lcl-tracking) 复用的是 TrackingPage，而集运板块的
   * /tracking 仍在使用同一个组件，所以 TrackingPage 没有标记下线，不要误删。
   */
  // {
  //   label: '散货板块',
  //   icon: FaShip,
  //   children: [
  //     { path: '/freight-evaluation', label: '运费评估' },
  //     { path: '/cargo-search', label: '货物查询' },
  //     { path: '/lcl-tracking', label: '轨迹查询' },
  //     { path: '/cargo-sign', label: '签收确认' },
  //     { path: '/cargo-claim', label: '货物认领' },
  //   ]
  // },
  /*
   * 【「订单板块」已撤销 · 2026-07-31】
   * 产品决定不再设「订单板块」这个入口，它原有的两个页面（订单下单、我的订单）
   * 已归入 userNav 的「我的」分组。两个页面本身照常在线，只是换了归属。
   * 这里不保留注释掉的旧配置 —— 板块是撤销而非下线，不存在原样恢复的场景。
   */
  // 面包屑保留「消息中心」：那是页面自己的标题，导航上叫「实时通知」是入口用语，
  // 两者刻意不同。要统一的话删掉 breadcrumbLabel 即可。
  { path: '/notifications', label: '实时通知', icon: FaBell, breadcrumbLabel: '消息中心' },
  /*
   * 【「财务管理」下拉已撤销 · 2026-08-01】
   *
   * 原先这里是个七项下拉（付款/充值/对账/交易记录/付款记录/收汇服务/融资服务），
   * 但七项全都指向同一个 /portal/finance，只是带不同的 ?tab= —— 下拉在这里
   * 扮演的其实是页内 tab 的角色。
   *
   * 而这本来就是页内 tab：2026-02-24 的 FinancePage 有一排 tab 按钮，
   * 2026-03-02 的 8a85c84 把它删掉、换成了这个导航下拉。现已改回页内 tab
   * （见 FinancePage.jsx 的 financeTabs），本处随之收敛为单个入口，
   * 并按「面向登录用户自己」的定位移入下方 userNav。
   *
   * 不保留注释掉的旧配置：下拉是撤销而非下线，不存在原样恢复的场景；
   * 真要回看，git show 8a85c84 即可。
   */
  { path: '/about', label: '关于我们', icon: FaInfoCircle },
]

/*
 * 账号下拉菜单（仅登录后渲染，桌面端与移动端共用这一份）。
 *
 * 「我的」保留为菜单内的一层分组，不摊平 —— 它是一个语义集合（用户自己的业务页面），
 * 与「客户中心」「问题反馈」这两个单页并列时需要有边界，否则五项平铺看不出层次。
 *
 * 带 children 的项渲染成可展开分组：点击标题展开/收起，默认收起（见 Navbar 的
 * expandedUserGroup 与它的重置 effect）。用点击而不是悬停：这已经是下拉里的第二层，
 * 悬停展开在下拉面板里极易误触，也不适配移动端 —— 移动端复用的正是这份配置。
 *
 * 【「我的」板块已并入账号菜单 · 2026-08-01】
 * 「我的」2026-07-31 建为导航板块，一天后并入这里 —— 它的定位本就是「面向登录用户
 * 自己的入口集合」，与账号菜单完全重合，两处并列反而要用户先猜自己的东西在哪一边。
 * 合并后语义单一：个人相关的一律在账号菜单里。连带效果：账号菜单本就只在
 * isAuthenticated 时渲染，所以这些入口自动跟「客户中心」一样登录后才可见。
 */
export const userNav = [
  { path: '/dashboard', label: '客户中心', icon: FaTachometerAlt },
  {
    label: '我的',
    icon: FaUserCircle,
    children: [
      { path: '/order-create', label: '订单下单', icon: FaPlusCircle },
      { path: '/my-orders', label: '我的订单', icon: FaBox },
      // 不带 ?tab= ，落在账户概览；其余七个 tab 在页内切换。
      // 下面七个 /finance/* 是 PlaceholderPage（页面建设中）的独立路由，不是这些 tab。
      {
        path: '/portal/finance',
        label: '财务管理',
        icon: FaWallet,
        aliases: ['/finance'],
        children: [
          { path: '/finance/payment', label: '付款' },
          { path: '/finance/recharge', label: '充值' },
          { path: '/finance/reconciliation', label: '对账' },
          { path: '/finance/transactions', label: '交易记录' },
          { path: '/finance/payment-records', label: '付款记录' },
          { path: '/finance/collection-service', label: '收汇服务' },
          { path: '/finance/financing-service', label: '融资服务' },
        ],
      },
    ],
  },
  { path: '/issues', label: '问题反馈', icon: FaHeadset },
]

/** 不进导航、但需要面包屑的页面 */
export const extraNav = [
  { path: '/faq', label: '常见问题' },
  { path: '/terms', label: '服务条款' },
  { path: '/privacy', label: '隐私政策' },
]

/**
 * 页脚栏目。只定分组与顺序，label 一律从上面的节点取，避免第二份文案。
 * paths 里写的是节点 path（别名不认，写主 path）。
 */
export const footerSections = [
  { title: '集运板块', groupLabel: '集运板块' },
  /*
   * 【散货拼柜栏目已下线 · 2026-07-30】随散货板块一并注释保留，见 mainNav 里的说明。
   * 恢复时把 mainNav 那段取消注释，并在此加回：{ title: '散货拼柜', groupLabel: '散货板块' }
   */
  {
    title: '帮助中心',
    paths: ['/', '/about', '/faq', '/terms', '/privacy'],
  },
]

// ---------------------------------------------------------------------------
// 查询辅助（纯函数，不依赖 React；面包屑走 hooks/useBreadcrumb.js）
// ---------------------------------------------------------------------------

const NAV_ROOTS = [mainNav, userNav, extraNav]

function walk(nodes, pathname, trail) {
  for (const node of nodes) {
    const next = [...trail, node]
    if (node.path === pathname || node.aliases?.includes(pathname)) {
      return next
    }
    if (node.children) {
      const hit = walk(node.children, pathname, next)
      if (hit) return hit
    }
  }
  return null
}

/** 按路由查出「祖先…自身」的节点链；查不到返回 null */
export function findNavTrail(pathname) {
  for (const roots of NAV_ROOTS) {
    const hit = walk(roots, pathname, [])
    if (hit) return hit
  }
  return null
}

/** 按路由查单个节点 */
export function findNavNode(pathname) {
  const trail = findNavTrail(pathname)
  return trail ? trail[trail.length - 1] : null
}

/** 按 label 查分组节点（用于页脚按栏目取整组子项） */
export function findNavGroup(label) {
  const hit = (nodes) => {
    for (const node of nodes) {
      if (node.label === label && node.children) return node
      if (node.children) {
        const deeper = hit(node.children)
        if (deeper) return deeper
      }
    }
    return null
  }
  for (const roots of NAV_ROOTS) {
    const found = hit(roots)
    if (found) return found
  }
  return null
}

/** 页脚栏目 → { title, links: [{ label, path }] } */
export function getFooterSections() {
  return footerSections.map((section) => ({
    title: section.title,
    links: section.groupLabel
      ? (findNavGroup(section.groupLabel)?.children ?? []).map((n) => ({ label: n.label, path: n.path }))
      : section.paths
        .map((p) => findNavNode(p))
        .filter(Boolean)
        .map((n) => ({ label: n.label, path: n.path })),
  }))
}
